export const roundMoney = (value) => Math.round((Number(value || 0) + Number.EPSILON) * 100) / 100;

export const getNumericAnswer = (answers, key) => {
  const value = Number(answers?.[key]);
  return Number.isFinite(value) ? value : 0;
};

export const isQuestionVisible = (question, answers) => {
  if (!question?.showWhen?.questionId) return true;
  const actual = answers?.[question.showWhen.questionId];
  const expected = question.showWhen.optionId;
  return Array.isArray(actual) ? actual.includes(expected) : actual === expected;
};

const getMultiplier = ({ priceType, answers, topic, option }) => {
  const unitQuestionId = option?.unitQuestionId || topic?.primaryQuantityQuestionId || "";
  const units = getNumericAnswer(answers, unitQuestionId);

  switch (priceType) {
    case "perUnit":
      return units;
    case "perSection": {
      const sectionLength = Number(option?.sectionLength || topic?.defaultSectionLength || 1);
      return sectionLength > 0 ? Math.ceil(units / sectionLength) : 0;
    }
    case "percent":
      return Number(option?.percentBase || 0) / 100;
    case "flat":
    default:
      return 1;
  }
};

export const calculateEstimate = ({ topic, questions, answers }) => {
  const lineItems = [];
  const materialMap = new Map();

  const visibleQuestions = (questions || []).filter((q) => isQuestionVisible(q, answers));

  visibleQuestions.forEach((question) => {
    const answer = answers?.[question.id];
    if (answer === undefined || answer === null || answer === "") return;

    const selectedIds = Array.isArray(answer) ? answer : [answer];
    const selectedOptions = (question.options || []).filter((option) => selectedIds.includes(option.id));

    selectedOptions.forEach((option) => {
      const priceType = option.priceType || "flat";
      const multiplier = getMultiplier({ priceType, answers, topic, option });
      const amount = priceType === "none"
        ? 0
        : roundMoney(Number(option.price || 0) * multiplier);

      if (amount !== 0) {
        lineItems.push({
          question: question.label,
          description: option.label,
          priceType,
          quantity: multiplier,
          unitPrice: Number(option.price || 0),
          amount,
        });
      }

      (option.materials || []).forEach((material) => {
        const formula = material.formula || "flat";
        let materialMultiplier = 1;

        if (formula === "perSection") {
          const unitQuestionId = material.unitQuestionId || option.unitQuestionId || topic?.primaryQuantityQuestionId;
          const units = getNumericAnswer(answers, unitQuestionId);
          const sectionLength = Number(material.sectionLength || option.sectionLength || topic?.defaultSectionLength || 1);
          materialMultiplier = sectionLength > 0 ? Math.ceil(units / sectionLength) : 0;
        } else if (formula === "perUnit") {
          const unitQuestionId = material.unitQuestionId || option.unitQuestionId || topic?.primaryQuantityQuestionId;
          materialMultiplier = getNumericAnswer(answers, unitQuestionId);
        }

        const quantity = roundMoney(Number(material.qty || 0) * materialMultiplier);
        if (!quantity) return;

        const key = `${material.name}|${material.unit || "each"}`;
        const current = materialMap.get(key) || {
          name: material.name,
          unit: material.unit || "each",
          quantity: 0,
        };
        current.quantity = roundMoney(current.quantity + quantity);
        materialMap.set(key, current);
      });
    });
  });

  const subtotal = roundMoney(lineItems.reduce((sum, item) => sum + item.amount, 0));
  const contingencyPercent = Number(topic?.contingencyPercent || 0);
  const contingency = roundMoney(subtotal * (contingencyPercent / 100));
  const total = roundMoney(subtotal + contingency);

  return {
    lineItems,
    materials: Array.from(materialMap.values()).sort((a, b) => a.name.localeCompare(b.name)),
    subtotal,
    contingency,
    contingencyPercent,
    total,
  };
};

export const formatMoney = (value) =>
  Number(value || 0).toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
  });
